import { useMemo } from "react"

import { Badge } from "@ui/badge"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Textarea } from "@ui/textarea"

import { CollectionItem, ContentObjectItem } from "@vertesia/common"

interface ReportCreationSummaryPanelProps {
    title: string
    setTitle: (value: string) => void
    description: string
    setDescription: (value: string) => void
    configuration: CollectionItem
    template: ContentObjectItem
    documents: ContentObjectItem[]
    additional: ContentObjectItem[]
    details: Record<string, string>
    setDetails: (additional: Record<string, string>) => void
    onNavigate: (step: "details" | "type" | "documents" | "additional" | "additional_details" | "summary" | "done") => void
}
export function ReportCreationSummaryPanel({ title, setTitle, description, setDescription, configuration, template, documents, additional, details, setDetails, onNavigate }: Readonly<ReportCreationSummaryPanelProps>) {
    const additionalFields = (configuration as any)?.properties?.additional || []
    
    const documentsTypes = useMemo(() => {
        if (!documents?.length) {
            return []
        }

        const types: any[] = documents.map((doc) => doc.type)
        return Object.values(
            types.reduce((acc, item) => {
                if (acc[item.id]) {
                    acc[item.id].count++
                } else {
                    acc[item.id] = { ...item, count: 1 }
                }
                return acc
            }, {})
        ).sort((a: any, b: any) => a.name.localeCompare(b.name))
    }, [documents])

    const additionalTypes = useMemo(() => {
        if (!additional?.length) {
            return []
        }

        const types: any[] = additional.map((doc) => doc.type)
        return Object.values(
            types.reduce((acc, item) => {
                if (acc[item.id]) {
                    acc[item.id].count++
                } else {
                    acc[item.id] = { ...item, count: 1 }
                }
                return acc
            }, {})
        ).sort((a: any, b: any) => a.name.localeCompare(b.name))
    }, [additional])

    return (
        <div className="flex flex-col h-full gap-3 overflow-scroll">
            <div>
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Report Title
                </Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Report title"/>
            </div>
            <div>
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Report Description
                </Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Report description" rows={3} className="resize-none" />
            </div>
            <div>
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Report Type <span className="text-xs font-light text-blue-500 hover:cursor-pointer hover:underline hover:text-blue-700" onClick={() => onNavigate("type")}>(edit)</span>
                </Label>
                <Input value={configuration.name} placeholder="Report type" disabled/>
            </div>
            <div>
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Report Template <span className="text-xs font-light text-blue-500 hover:cursor-pointer hover:underline hover:text-blue-700" onClick={() => onNavigate("type")}>(edit)</span>
                </Label>
                <Input value={template?.properties?.name || template?.name || template.id} placeholder="Report template" disabled/>
            </div>
            <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Selected Document{documents.length > 1 && "s"} ({documents.length}) <span className="text-xs font-light text-blue-500 hover:cursor-pointer hover:underline hover:text-blue-700" onClick={() => onNavigate("documents")}>(edit)</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                    {
                        documentsTypes.map((type: any) => (
                            <Badge key={type.id}>
                                {type.name} ({type.count})
                            </Badge>
                        ))
                    }
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Additional Document{additional.length > 1 && "s"} ({additional.length}) <span className="text-xs font-light text-blue-500 hover:cursor-pointer hover:underline hover:text-blue-700" onClick={() => onNavigate("additional")}>(edit)</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                    {
                        additionalTypes.map((type: any) => (
                            <Badge key={type.id}>
                                {type.name} ({type.count})
                            </Badge>
                        ))
                    }
                </div>
            </div>
            {
                additionalFields.length > 0 && (
                    <div>
                        <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Template Information{Object.entries(details).length > 1 && "s"}
                        </Label>
                        <div className="flex flex-col gap-1">
                            {
                                additionalFields.map((field: any, index: number) => (
                                    <div key={index} className="flex flex-col h-full">
                                        <Label className="text-xs font-light leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                            {field.name}{field.required ? <span className="text-red-500">*</span> : <span className="text-muted-foreground"> (optional)</span>}
                                        </Label>
                                        <Textarea value={details[field.name] || ""} onChange={(e) => setDetails({ ...details, [field.name]: e.target.value })} placeholder={field.placeholder} rows={2} className="resize-none" />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )
            }
        </div>
    )
}
